import { AmieAction } from '../../../core/actions/action.model';
import { CardMember, CustomerCard, EntitySection } from './unified-search.model';
import { phoneticEquals } from './cologne-phonetic';

/**
 * Rank an action against a query. Higher = better. 0 = no match.
 * Matches title (prefix > substring), then subtitle/keywords — so Plan 2's
 * keyword-less customer/UA/PS actions are still reachable by title/subtitle.
 */
function scoreAction(action: AmieAction, q: string): number {
  const title = action.title.toLowerCase();
  const subtitle = (action.subtitle ?? '').toLowerCase();
  const keywords = (action.keywords ?? []).map((k) => k.toLowerCase());
  if (title.startsWith(q)) return 3;
  if (title.includes(q)) return 2;
  if (subtitle.includes(q) || keywords.some((k) => k.includes(q))) return 1;
  return 0;
}

export function matchActions(actions: AmieAction[], query: string): AmieAction[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return actions
    .map((action) => ({ action, score: scoreAction(action, q) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score) // Array.sort is stable → equal scores keep input order
    .map((x) => x.action);
}

const STATUS_RANK: Record<string, number> = {
  active: 0, aktiv: 0,
  futureCustomer: 1, futureService: 1,
  formerCustomer: 2, formerService: 2,
  deactivated: 3, deaktiv: 3, neverActiveService: 3,
};
function statusRank(s: string): number { return STATUS_RANK[s] ?? 2; }

export type CardSortMode = 'relevance' | 'status';

/**
 * Re-order already-relevance-ranked cards by the chosen sort. 'relevance' is a no-op (the
 * incoming order already carries the composite relevance score). 'status' groups active-first,
 * keeping the relevance order within each status bucket (stable).
 */
export function sortCards(cards: CustomerCard[], mode: CardSortMode): CustomerCard[] {
  if (mode === 'relevance') return cards;
  return cards
    .map((c, i) => ({ c, i }))
    .sort((a, b) => statusRank(a.c.status) - statusRank(b.c.status) || a.i - b.i)
    .map((x) => x.c);
}

/** Composite ranking: exact id > exact name > name substring > phonetic; then active-first; stable. */
export function rankCards(cards: CustomerCard[], query: string): CustomerCard[] {
  const q = query.trim();
  if (!q) return cards;
  const ql = q.toLowerCase();
  const tokens = q.split(/\s+/);
  const phonetic = (name: string) => name.split(/\s+/).some((w) => tokens.some((t) => phoneticEquals(w, t)));
  const score = (c: CustomerCard): number => {
    let s = 0;
    if (c.customerId === q) s += 1000;
    else if (c.name.toLowerCase() === ql) s += 800;
    else if (c.name.toLowerCase().includes(ql)) s += 200;
    else if (phonetic(c.name)) s += 100;
    s -= statusRank(c.status) * 10;
    return s;
  };
  return cards.map((c, i) => ({ c, i, s: score(c) }))
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((x) => x.c);
}

/** True when the card's NAME only matches phonetically (not as a substring) — used to mark it. */
export function isPhoneticOnlyMatch(card: CustomerCard, query: string): boolean {
  const q = query.trim();
  if (!q) return false;
  const name = card.name.toLowerCase();
  if (name.includes(q.toLowerCase())) return false;
  const tokens = q.split(/\s+/);
  return card.name.split(/\s+/).some((w) => tokens.some((t) => phoneticEquals(w, t)));
}

export interface HighlightSegment { text: string; hit: boolean; }

/** Split text into segments, marking case-insensitive occurrences of query as hits. Empty query → one non-hit segment. */
export function highlightSegments(text: string, query: string): HighlightSegment[] {
  const q = query.trim();
  if (!q || !text) return [{ text, hit: false }];
  const segments: HighlightSegment[] = [];
  const lower = text.toLowerCase();
  const ql = q.toLowerCase();
  // Offsets are found on `lower` but sliced from `text`, which is only safe while lowercasing
  // preserves length/positions. Length-changing case folding (e.g. Turkish İ U+0130 → "i̇", two code
  // units) would misalign the slices, so bail to a single non-hit segment rather than mangle the text.
  if (lower.length !== text.length) return [{ text, hit: false }];
  let i = 0;
  while (i < text.length) {
    const idx = lower.indexOf(ql, i);
    if (idx === -1) { segments.push({ text: text.slice(i), hit: false }); break; }
    if (idx > i) segments.push({ text: text.slice(i, idx), hit: false });
    segments.push({ text: text.slice(idx, idx + ql.length), hit: true });
    i = idx + ql.length;
  }
  return segments;
}

/**
 * A child (UA/PS) is a genuine match only if the query hits its OWN text/id — not merely the
 * shared customerId.
 *
 * `login` and `email` are part of the haystack because the backend matches on them
 * (`entities.login` / `entities.email`) and they are not derivable from the label: searching
 * "torsten.lenz" finds the account labelled "Herr Torsten Lenz", whose label contains no dot.
 * Leaving them out silently collapsed every username hit back to a company-only card.
 */
function childMatches(section: EntitySection, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [section.label, section.sublabel ?? '', section.login ?? '', section.email ?? '', section.entityId]
    .join(' ')
    .toLowerCase();
  if (hay.includes(q)) return true;
  const tokens = query.trim().split(/\s+/);
  return section.label.split(/\s+/).some((w) => tokens.some((t) => phoneticEquals(w, t)));
}

/**
 * Keep only UA/PS sections that genuinely matched the query. When the query hit only the
 * company (e.g. a customerId or company-name lookup pulls in all children via shared
 * customerId), this collapses the card back to a clean company-only card.
 *
 * Linked partners of a genuine match are preserved so a member row is never shown half-empty:
 * a matched UA keeps its linked PS, and a matched PS keeps the UA it links back to — even when
 * that partner's own text didn't match the query.
 */
export function filterCardChildren(card: CustomerCard, query: string): CustomerCard {
  const keepUaIds = new Set(card.userAccounts.filter((ua) => childMatches(ua, query)).map((ua) => ua.entityId));
  const keepPsIds = new Set(card.phoneServices.filter((ps) => childMatches(ps, query)).map((ps) => ps.entityId));

  // A matched PS pulls in the UA it links back to…
  for (const ps of card.phoneServices) {
    if (keepPsIds.has(ps.entityId) && ps.linkedUserAccountId) keepUaIds.add(ps.linkedUserAccountId);
  }
  // …and a kept UA pulls in the PS that links to it.
  for (const ps of card.phoneServices) {
    if (ps.linkedUserAccountId && keepUaIds.has(ps.linkedUserAccountId)) keepPsIds.add(ps.entityId);
  }

  const userAccounts = card.userAccounts.filter((ua) => keepUaIds.has(ua.entityId));
  const phoneServices = card.phoneServices.filter((ps) => keepPsIds.has(ps.entityId));
  return { ...card, userAccounts, phoneServices, hasUserAccount: userAccounts.length > 0, hasPhoneService: phoneServices.length > 0 };
}

/**
 * Pairs UA and PS sections 1:1 into member rows for the result card (EB-3621 card redesign):
 * each user account has 0 or 1 phone service in reality. Walks the UAs in order, matching each
 * to the first not-yet-used PS whose `linkedUserAccountId` points back to it (1:1 / 1:0); any PS
 * left over (no matching UA) becomes its own orphan member (0:1).
 */
export function pairMembers(userAccounts: EntitySection[], phoneServices: EntitySection[]): CardMember[] {
  const usedPsIds = new Set<string>();
  const members: CardMember[] = userAccounts.map((ua) => {
    const ps = phoneServices.find((p) => !usedPsIds.has(p.entityId) && p.linkedUserAccountId === ua.entityId);
    if (ps) usedPsIds.add(ps.entityId);
    return { userAccount: ua, phoneService: ps };
  });
  for (const ps of phoneServices) {
    if (!usedPsIds.has(ps.entityId)) members.push({ phoneService: ps });
  }
  return members;
}
